import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * MAISON ARPENT — L'ARPENTAGE, simulateur d'échelle.
 * ---------------------------------------------------------------------------
 * Répond au frein n°1 de la niche : « je n'arrive pas à me représenter
 * 200 × 300 dans MON salon ». On dessine la pièce du client à ses vraies
 * dimensions, on y pose le tapis à l'échelle, et on rend un verdict chiffré.
 * Le verdict est aussi un moteur de recommandation : il désigne le bon format
 * et permet de le sélectionner en un clic.
 *
 * Ce qui vit ICI (structure) : le dessin du mobilier en centimètres réels, la
 * géométrie du verdict, les quatre seuils et leurs phrases.
 * Ce qui vit dans les RÉGLAGES (contenu) : les pièces (nom, dimensions,
 * conseil), tous les intitulés, les couleurs.
 * Ce qui vit dans le CATALOGUE : les formats, leurs dimensions, leurs images —
 * lus une fois dans le tableau Liquid `ref="donnees"`, jamais dans la charge de
 * l'événement `productSelect`.
 *
 * @typedef {object} Variante
 * @property {number} id
 * @property {string} taille
 * @property {string} coloris
 * @property {number} largeur
 * @property {number} longueur
 * @property {string} forme
 * @property {number} prix
 * @property {string} image
 *
 * @typedef {object} PieceReglee
 * @property {string} cle
 * @property {string} nom
 * @property {string} gabarit
 * @property {number[]} defaut
 * @property {number[]} min
 * @property {number[]} max
 * @property {string} conseil
 *
 * @typedef {object} Format
 * @property {string} taille
 * @property {number} l  largeur du tapis, en cm
 * @property {number} L  longueur du tapis, en cm
 * @property {boolean} rond
 *
 * @typedef {object} Refs
 * @property {HTMLScriptElement} donnees
 * @property {HTMLElement} plan
 * @property {HTMLElement} scene
 * @property {SVGSVGElement} dessin
 * @property {SVGGElement} gMeubles
 * @property {SVGRectElement} tapisForme
 * @property {SVGRectElement} [tapisClip]
 * @property {SVGImageElement} [tapisImage]
 * @property {HTMLElement} [coteH]
 * @property {HTMLElement} [coteV]
 * @property {HTMLInputElement} [largeur]
 * @property {HTMLInputElement} [longueur]
 * @property {HTMLElement} [valLargeur]
 * @property {HTMLElement} [valLongueur]
 * @property {HTMLElement} verdict
 * @property {HTMLElement} [verdictTitre]
 * @property {HTMLElement} [verdictTexte]
 * @property {HTMLElement} [mesureSurface]
 * @property {HTMLElement} [mesureSol]
 * @property {HTMLElement} [mesureMur]
 * @property {HTMLButtonElement} [adopter]
 * @property {HTMLElement} [adopterTexte]
 * @property {HTMLElement} [conseil]
 *
 * @extends {Component<Refs>}
 */

const NS = 'http://www.w3.org/2000/svg';

/**
 * @param {string} type
 * @param {Record<string, string | number>} [attributs]
 * @returns {SVGElement}
 */
const el = (type, attributs = {}) => {
  const noeud = document.createElementNS(NS, type);
  for (const cle in attributs) noeud.setAttribute(cle, String(attributs[cle]));
  return noeud;
};

/* ---- Dessin du mobilier, en centimètres réels ----------------------------
   Porté tel quel du MVP. La couleur vient de `currentColor`, donc du CSS,
   donc des réglages : rien de codé en dur ici. */
const TRAIT = {
  fill: 'none',
  stroke: 'currentColor',
  'stroke-width': 2.2,
  'stroke-linejoin': 'round',
  'vector-effect': 'non-scaling-stroke',
};
const FIN = { ...TRAIT, 'stroke-width': 1.4, opacity: 0.55 };

const MEUBLES = {
  /** @param {SVGElement} g @param {any} m */
  canape(g, { x, y, w, h }) {
    g.append(el('rect', { x, y, width: w, height: h, rx: 6, ...TRAIT }));
    g.append(el('rect', { x: x + 2, y: y + 2, width: w - 4, height: h * 0.34, rx: 4, ...FIN })); // dossier
    g.append(el('rect', { x, y: y + h * 0.34, width: 22, height: h * 0.66, rx: 5, ...FIN })); // accoudoirs
    g.append(el('rect', { x: x + w - 22, y: y + h * 0.34, width: 22, height: h * 0.66, rx: 5, ...FIN }));
    for (let i = 0; i < 3; i++) {
      // assises
      g.append(
        el('rect', {
          x: x + 24 + i * ((w - 48) / 3),
          y: y + h * 0.36,
          width: (w - 48) / 3 - 3,
          height: h * 0.6,
          rx: 4,
          ...FIN,
        })
      );
    }
  },
  /** @param {SVGElement} g @param {any} m */
  fauteuil(g, { x, y, w, h }) {
    g.append(el('rect', { x, y, width: w, height: h, rx: 8, ...TRAIT }));
    g.append(el('rect', { x: x + 3, y: y + 3, width: w - 6, height: h * 0.3, rx: 5, ...FIN }));
    g.append(el('rect', { x: x + 7, y: y + h * 0.36, width: w - 14, height: h * 0.56, rx: 5, ...FIN }));
  },
  /** @param {SVGElement} g @param {any} m */
  tableBasse(g, { x, y, w, h }) {
    g.append(el('rect', { x, y, width: w, height: h, rx: 4, ...TRAIT }));
    g.append(el('rect', { x: x + 9, y: y + 9, width: w - 18, height: h - 18, rx: 3, ...FIN }));
  },
  /** @param {SVGElement} g @param {any} m */
  lit(g, { x, y, w, h }) {
    g.append(el('rect', { x, y, width: w, height: h, rx: 5, ...TRAIT }));
    g.append(el('rect', { x: x - 3, y: y - 8, width: w + 6, height: 12, rx: 3, ...TRAIT })); // tête
    g.append(el('rect', { x: x + 8, y: y + 10, width: w / 2 - 14, height: 38, rx: 5, ...FIN })); // oreillers
    g.append(el('rect', { x: x + w / 2 + 6, y: y + 10, width: w / 2 - 14, height: 38, rx: 5, ...FIN }));
    g.append(el('path', { d: `M${x} ${y + h * 0.52} H${x + w}`, ...FIN })); // plaid
  },
  /** @param {SVGElement} g @param {any} m */
  chevet(g, { x, y, w, h }) {
    g.append(el('rect', { x, y, width: w, height: h, rx: 3, ...FIN }));
  },
  /** @param {SVGElement} g @param {any} m */
  table(g, { x, y, w, h }) {
    g.append(el('rect', { x, y, width: w, height: h, rx: 4, ...TRAIT }));
    g.append(el('rect', { x: x + 10, y: y + 10, width: w - 20, height: h - 20, rx: 3, ...FIN }));
  },
  /** @param {SVGElement} g @param {any} m */
  chaise(g, { x, y, w, h, r }) {
    const gg = el('g', r ? { transform: `rotate(180 ${x + w / 2} ${y + h / 2})` } : {});
    gg.append(el('rect', { x, y, width: w, height: h, rx: 5, ...FIN }));
    gg.append(el('rect', { x: x + 3, y: y + h - 9, width: w - 6, height: 7, rx: 3, ...FIN }));
    g.append(gg);
  },
  /** @param {SVGElement} g @param {any} m */
  bureau(g, { x, y, w, h }) {
    g.append(el('rect', { x, y, width: w, height: h, rx: 3, ...TRAIT }));
    g.append(el('rect', { x: x + w - 46, y: y + 8, width: 34, height: 22, rx: 2, ...FIN }));
  },
  /** @param {SVGElement} g @param {any} m */
  siege(g, { x, y, w, h }) {
    g.append(el('circle', { cx: x + w / 2, cy: y + h / 2, r: w / 2, ...FIN }));
    g.append(el('rect', { x: x + 6, y: y + 6, width: w - 12, height: h - 12, rx: 6, ...FIN }));
  },
};

/* ---- Gabarits de mobilier ------------------------------------------------
   L'algorithme de disposition est de la structure : il reste en JS. Les
   dimensions et les textes de chaque pièce, eux, sont des réglages de bloc.
   `ancre` = la zone que le tapis doit couvrir pour que la pièce « tienne ». */
const GABARITS = {
  salon: {
    meubles: (L, P) => {
      const cx = L / 2;
      const y0 = Math.max(40, P * 0.13);
      return [
        { t: 'canape', x: cx - 115, y: y0, w: 230, h: 95 },
        { t: 'tableBasse', x: cx - 60, y: y0 + 150, w: 120, h: 65 },
        { t: 'fauteuil', x: cx - 190, y: y0 + 128, w: 80, h: 80 },
        { t: 'fauteuil', x: cx + 110, y: y0 + 128, w: 80, h: 80 },
      ];
    },
    /* la ligne des pieds avant du canapé, la table basse, et 40 cm devant elle */
    ancre: (L, P) => {
      const y0 = Math.max(40, P * 0.13);
      return { x: L / 2 - 105, y: y0 + 65, w: 210, h: 200 };
    },
    centreY: (L, P) => Math.max(40, P * 0.13) + 60 + 79,
  },
  chambre: {
    meubles: (L, P) => {
      const cx = L / 2;
      const y0 = Math.max(20, P * 0.07);
      return [
        { t: 'lit', x: cx - 80, y: y0, w: 160, h: 200 },
        { t: 'chevet', x: cx - 127, y: y0, w: 44, h: 44 },
        { t: 'chevet', x: cx + 83, y: y0, w: 44, h: 44 },
      ];
    },
    /* la moitié basse du lit, plus 70 cm au-delà du pied */
    ancre: (L, P) => {
      const y0 = Math.max(20, P * 0.07);
      return { x: L / 2 - 100, y: y0 + 90, w: 200, h: 180 };
    },
    centreY: (L, P) => Math.max(20, P * 0.07) + 165,
  },
  'salle-a-manger': {
    meubles: (L, P) => {
      const cx = L / 2;
      const ty = Math.max(90, P * 0.24);
      const tw = 180;
      const th = 90;
      const out = [{ t: 'table', x: cx - tw / 2, y: ty, w: tw, h: th }];
      for (let i = 0; i < 3; i++) {
        out.push({ t: 'chaise', x: cx - tw / 2 + 13 + i * 58, y: ty - 48, w: 46, h: 46, r: 180 });
        out.push({ t: 'chaise', x: cx - tw / 2 + 13 + i * 58, y: ty + th + 2, w: 46, h: 46 });
      }
      return out;
    },
    /* la table, plus le recul des chaises devant et derrière */
    ancre: (L, P) => {
      const ty = Math.max(90, P * 0.24);
      return { x: L / 2 - 115, y: ty - 70, w: 230, h: 230 };
    },
    centreY: (L, P) => Math.max(90, P * 0.24) + 45,
  },
  bureau: {
    meubles: (L, P) => {
      const cx = L / 2;
      const y0 = Math.max(30, P * 0.16);
      return [
        { t: 'bureau', x: cx - 70, y: y0, w: 140, h: 70 },
        { t: 'siege', x: cx - 30, y: y0 + 88, w: 60, h: 60 },
      ];
    },
    ancre: (L, P) => {
      const y0 = Math.max(30, P * 0.16);
      return { x: L / 2 - 95, y: y0, w: 190, h: 170 };
    },
    centreY: (L, P) => Math.max(30, P * 0.16) + 85,
  },
};

/* ---- Géométrie ----------------------------------------------------------- */

/**
 * Aire d'intersection de deux rectangles, en cm².
 * @param {{x:number,y:number,w:number,h:number}} a
 * @param {{x:number,y:number,w:number,h:number}} b
 */
const inter = (a, b) =>
  Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x)) *
  Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));

/** @param {number} cm → « 4,30 » */
const metres = (cm) => (cm / 100).toFixed(2).replace('.', ',');

/** @param {number} n @param {number} min @param {number} max */
const borner = (n, min, max) => Math.min(Math.max(n, min), max);

class ArpentageComponent extends Component {
  requiredRefs = ['donnees', 'plan', 'scene', 'dessin', 'gMeubles', 'tapisForme', 'verdict'];

  /** @type {{produitId:string, optionTaille:string, optionColoris:string, pieces:PieceReglee[], variantes:Variante[], conseilProduit:string, pieceOuverte:string, tailleInitiale:string, colorisInitial:string} | null} */
  #donnees = null;

  /** @type {Format[]} */
  #formats = [];

  /** @type {{piece:PieceReglee|null, L:number, P:number, taille:string, coloris:string, pivote:boolean}} */
  #etat = { piece: null, L: 0, P: 0, taille: '', coloris: '', pivote: false };

  /** @type {Format | null} Le format que le verdict recommande. */
  #ideal = null;

  /** @type {Element | Document | null} Le conteneur qui relaie `productSelect`. */
  #contexte = null;

  /** Garde anti-boucle : vrai pendant que NOUS pilotons le sélecteur de variante. */
  #interne = false;

  /** Le tout premier rendu ne doit pas être annoncé par le lecteur d'écran. */
  #premierRendu = true;

  /** @type {ResizeObserver | undefined} */
  #observateur;

  /** @type {number} */
  #largeurPlan = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#lireDonnees();
    if (!this.#donnees || !this.#formats.length) return;

    /*
      Le repli est `document`, jamais la section du bloc.

      Sur la fiche produit réelle, l'Arpentage est une SECTION À PART (`…__arpentage`)
      tandis que le sélecteur de variante vit dans `…__main`. Se scoper à sa propre
      section revenait donc à écouter un élément qui ne reçoit jamais l'événement : le
      plan ne suivait ni le format ni le coloris. Le défaut n'apparaissait pas sur un
      banc d'essai où les deux sont dans la même section.

      `shopify:product:select` bourgeonne jusqu'à `document` (vérifié en navigateur), et
      `#surVariante` filtre déjà sur l'identifiant du produit : écouter large est sûr.
    */
    this.#contexte =
      this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card') ??
      document;
    this.#contexte.addEventListener(StandardEvents.productSelect, this.#surVariante);

    this.#observateur = new ResizeObserver(() => {
      const plan = this.refs.plan;
      if (!plan) return;
      const largeur = plan.clientWidth;
      if (largeur === this.#largeurPlan) return;
      this.#largeurPlan = largeur;
      this.#dessiner();
    });
    this.#observateur.observe(this.refs.plan);

    this.#initialiser();
    this.#dessiner();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#contexte?.removeEventListener(StandardEvents.productSelect, this.#surVariante);
    this.#contexte = null;
    this.#observateur?.disconnect();
    this.#observateur = undefined;
  }

  /** Le rendu serveur a pu remplacer notre sous-arbre : on redessine. */
  updatedCallback() {
    super.updatedCallback();
    if (this.#donnees && this.#formats.length) this.#dessiner();
  }

  /* ==== Actions déclaratives (`on:` dans le Liquid) ======================= */

  /**
   * 1. La pièce.
   * @param {Event} event
   */
  choisirPiece(event) {
    const cible = event.target;
    const bouton = cible instanceof Element ? cible.closest('button[data-piece]') : null;
    const cle = bouton?.dataset.piece;
    const piece = this.#donnees?.pieces.find((p) => p.cle === cle);
    if (!piece) return;

    this.#etat.piece = piece;
    this.#etat.L = piece.defaut[0];
    this.#etat.P = piece.defaut[1];
    this.#majCurseurs();
    this.#dessiner();
  }

  /**
   * 3. Le format. Ne recode pas la sélection : délègue au `variant-picker`.
   * @param {Event} event
   */
  choisirFormat(event) {
    const cible = event.target;
    const bouton = cible instanceof Element ? cible.closest('button[data-taille]') : null;
    const taille = bouton?.dataset.taille;
    if (!taille || !this.#formats.some((f) => f.taille === taille)) return;
    this.#adopter(taille);
  }

  /**
   * Le sens de pose. Un 160 × 230 le long du canapé ou en travers, ce n'est pas
   * la même pièce — et c'est un choix réel, pas une variante. On échange donc
   * l'empreinte au sol, on fait pivoter l'image d'un quart de tour, et le
   * verdict se recalcule sur la nouvelle emprise : c'est tout l'intérêt.
   * Sans effet sur un tapis rond, où le bouton est masqué.
   */
  pivoter() {
    this.#etat.pivote = !this.#etat.pivote;
    this.#dessiner();
  }

  /**
   * Page « L'Arpentage » : on change de tapis sans changer de pièce.
   * La pièce, ses dimensions et le sens de pose sont gardés : c'est tout
   * l'intérêt de comparer plusieurs tapis dans la même pièce. Le format suit :
   * même taille si le nouveau tapis l'a, sinon celle que le verdict choisirait.
   * `brut` a la forme du tableau `ref="donnees"`, sans les pièces.
   * @param {any} brut
   */
  changerProduit(brut) {
    const actuel = this.#donnees;
    if (!actuel || !Array.isArray(brut?.variantes)) return false;
    const tailleAvant = this.#etat.taille;
    const source = this.refs.donnees;
    if (!source) return false;
    source.textContent = JSON.stringify({
      ...brut,
      pieces: actuel.pieces,
      pieceOuverte: this.#etat.piece?.cle ?? actuel.pieceOuverte,
    });
    this.#lireDonnees();
    if (!this.#formats.length) {
      source.textContent = JSON.stringify({ ...actuel, variantes: actuel.variantes });
      this.#lireDonnees();
      return false;
    }

    /* Les boutons de format : on clone le premier, rendu en Liquid. La
       liaison `on:click` est déléguée par le thème au niveau du document. */
    const modele = this.querySelector('button[data-taille]');
    const groupe = modele?.parentElement;
    if (modele && groupe) {
      const boutons = this.#formats.map((f) => {
        const b = /** @type {HTMLElement} */ (modele.cloneNode(true));
        b.dataset.taille = f.taille;
        const nom = b.querySelector('.__segment-nom');
        if (nom) nom.textContent = f.taille;
        const prix = b.querySelector('.__segment-prix');
        const v = this.#donnees?.variantes.find((x) => x.taille === f.taille);
        if (prix) prix.textContent = v?.prixTexte ?? '';
        return b;
      });
      groupe.replaceChildren(...boutons);
    }

    this.#etat.coloris = String(this.#donnees?.colorisInitial ?? '');
    this.#etat.taille = this.#meilleurFormat() ?? tailleAvant;
    this.#dessiner();
    return true;
  }

  /** Page « L'Arpentage » : poser le format que le verdict choisirait. */
  choisirIdeal() {
    const taille = this.#meilleurFormat();
    if (!taille || taille === this.#etat.taille) return;
    this.#etat.taille = taille;
    this.#dessiner();
  }

  /**
   * Le format que nous choisirions dans la pièce réglée : même règle que le
   * verdict (meilleure couverture de la zone de vie avec au moins 25 cm de
   * sol autour), dans le sens de pose choisi.
   * @returns {string | null}
   */
  #meilleurFormat() {
    const piece = this.#etat.piece;
    const { L, P } = this.#etat;
    if (!piece || !this.#formats.length || !(L > 0) || !(P > 0)) return null;
    const gabarit = GABARITS[piece.gabarit] ?? GABARITS.salon;
    const ancre = gabarit.ancre(L, P);
    const aireAncre = ancre.w * ancre.h;
    const candidats = this.#formats.map((c) => {
      const cpiv = this.#etat.pivote && !c.rond;
      const cw = cpiv ? c.L : c.l;
      const ch = cpiv ? c.l : c.rond ? c.l : c.L;
      const cx = (L - cw) / 2;
      const cy = gabarit.centreY(L, P) - ch / 2;
      return {
        taille: c.taille,
        couv: aireAncre > 0 ? inter({ x: cx, y: cy, w: cw, h: ch }, ancre) / aireAncre : 0,
        marge: Math.min(cx, L - (cx + cw), cy, P - (cy + ch)),
      };
    });
    const ideal =
      candidats.filter((c) => c.marge >= 25).sort((a, b) => b.couv - a.couv)[0] ??
      [...candidats].sort((a, b) => b.marge - a.marge)[0];
    return ideal?.taille ?? null;
  }

  /** Le bouton « Passer au … » du verdict. */
  adopterIdeal() {
    if (this.#ideal) this.#adopter(this.#ideal.taille);
  }

  /** @param {Event} event */
  majLargeur(event) {
    const champ = event.target;
    if (!(champ instanceof HTMLInputElement)) return;
    this.#etat.L = Number(champ.value) || this.#etat.L;
    this.#majCurseurs();
    this.#dessiner();
  }

  /** @param {Event} event */
  majLongueur(event) {
    const champ = event.target;
    if (!(champ instanceof HTMLInputElement)) return;
    this.#etat.P = Number(champ.value) || this.#etat.P;
    this.#majCurseurs();
    this.#dessiner();
  }

  /* ==== Données =========================================================== */

  /** Lit le tableau écrit une fois en Liquid. Aucune requête réseau. */
  #lireDonnees() {
    const contenu = this.refs.donnees?.textContent;
    if (!contenu) return;

    try {
      const brut = JSON.parse(contenu);
      if (!Array.isArray(brut?.variantes) || !Array.isArray(brut?.pieces)) return;
      this.#donnees = {
        produitId: String(brut.produitId ?? ''),
        optionTaille: String(brut.optionTaille ?? ''),
        optionColoris: String(brut.optionColoris ?? ''),
        pieces: brut.pieces,
        variantes: brut.variantes,
        conseilProduit: String(brut.conseilProduit ?? ''),
        pieceOuverte: String(brut.pieceOuverte ?? ''),
        tailleInitiale: String(brut.tailleInitiale ?? ''),
        colorisInitial: String(brut.colorisInitial ?? ''),
      };
    } catch {
      return;
    }

    /* Un format sans dimensions est écarté en silence : jamais de plan vide,
       jamais de NaN. */
    const vus = new Set();
    this.#formats = [];
    for (const v of this.#donnees.variantes) {
      const taille = String(v?.taille ?? '');
      if (!taille || vus.has(taille)) continue;
      const l = Number(v.largeur);
      const L = Number(v.longueur);
      if (!Number.isFinite(l) || !Number.isFinite(L) || l <= 0 || L <= 0) continue;
      vus.add(taille);
      this.#formats.push({ taille, l, L, rond: String(v.forme ?? '').toLowerCase().startsWith('rond') });
    }
  }

  #initialiser() {
    const d = this.#donnees;
    if (!d) return;

    const piece = d.pieces.find((p) => p.cle === d.pieceOuverte) ?? d.pieces[0];
    this.#etat.piece = piece ?? null;
    if (piece) {
      this.#etat.L = piece.defaut[0];
      this.#etat.P = piece.defaut[1];
    }

    const initial = this.#formats.find((f) => f.taille === d.tailleInitiale) ?? this.#formats[0];
    this.#etat.taille = initial ? initial.taille : '';
    this.#etat.coloris = d.colorisInitial;

    this.#majCurseurs();
  }

  /* ==== Le lien avec le sélecteur de variante ============================== */

  /** @returns {Element | null} */
  get #picker() {
    return (this.#contexte instanceof Element ? this.#contexte : document).querySelector('variant-picker');
  }

  /**
   * L'Arpentage pilote le sélecteur : on déclenche l'entrée native, jamais
   * une sélection maison. Le coloris courant est conservé — on ne touche
   * qu'à l'entrée de taille.
   * @param {string} taille
   */
  #adopter(taille) {
    /* Réponse optimiste : le plan bouge tout de suite, le serveur confirmera. */
    this.#etat.taille = taille;
    this.#dessiner();

    /* Sur la page « L'Arpentage », il n'y a pas de sélecteur à piloter. */
    if (this.closest('[data-arpentage-autonome]')) return;

    const picker = this.#picker;
    const nom = this.#donnees?.optionTaille;
    if (!picker || !nom) return;

    this.#interne = true;
    try {
      for (const entree of picker.querySelectorAll('input[type="radio"]')) {
        if (!(entree instanceof HTMLInputElement)) continue;
        if (entree.dataset.optionName !== nom || entree.value !== taille) continue;
        if (entree.checked) return;
        entree.checked = true;
        entree.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      for (const liste of picker.querySelectorAll('select')) {
        if (!(liste instanceof HTMLSelectElement)) continue;
        for (const choix of liste.options) {
          if (choix.dataset.optionName !== nom || choix.value !== taille) continue;
          if (liste.value === choix.value) return;
          liste.value = choix.value;
          liste.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }
    } finally {
      /* La garde ne couvre que l'aller-retour synchrone : `variant-picker`
         émet `productSelect` pendant le `dispatchEvent` ci-dessus. */
      this.#interne = false;
    }
  }

  /**
   * Le sélecteur de variante pilote l'Arpentage.
   * @param {Event & { promise?: Promise<{ detail: any }> }} event
   */
  #surVariante = (event) => {
    /* Aller synchrone : on ne dépend pas du réseau pour bouger le plan. */
    if (!this.#interne) this.#relireLePicker();

    /* Retour serveur : on se recale sur la variante réellement retenue —
       Shopify a pu changer le coloris si la combinaison n'existait pas. */
    const promesse = event?.promise;
    if (!promesse?.then) return;
    promesse
      .then(({ detail } = { detail: null }) => {
        if (!detail) return;
        const produitId = this.#donnees?.produitId;
        if (detail.productId && produitId && String(detail.productId) !== produitId) return;

        const id = detail.resource?.id;
        const variante = id != null ? this.#donnees?.variantes.find((v) => String(v.id) === String(id)) : null;
        if (variante) this.#appliquer(variante.taille, variante.coloris);
        else this.#relireLePicker();
      })
      .catch((erreur) => {
        if (erreur?.name !== 'AbortError') console.warn('[arpentage]', erreur);
      });
  };

  /** Lit les entrées cochées du sélecteur natif — jamais la charge de l'événement. */
  #relireLePicker() {
    const picker = this.#picker;
    if (!picker) return;

    /** @type {Record<string, string>} */
    const choisies = {};
    for (const liste of picker.querySelectorAll('select')) {
      if (!(liste instanceof HTMLSelectElement)) continue;
      const choix = liste.selectedOptions[0];
      const nom = choix instanceof HTMLOptionElement ? choix.dataset.optionName : undefined;
      if (nom) choisies[nom] = liste.value;
    }
    for (const entree of picker.querySelectorAll('fieldset input:checked')) {
      if (!(entree instanceof HTMLInputElement)) continue;
      const nom = entree.dataset.optionName;
      if (nom) choisies[nom] = entree.value;
    }

    const d = this.#donnees;
    if (!d) return;
    /* Une option à valeur unique n'a pas d'entrée dans le DOM : on garde l'état. */
    this.#appliquer(choisies[d.optionTaille] ?? this.#etat.taille, choisies[d.optionColoris] ?? this.#etat.coloris);
  }

  /**
   * @param {string} taille
   * @param {string} coloris
   */
  #appliquer(taille, coloris) {
    const connu = this.#formats.some((f) => f.taille === taille);
    /* Format sans dimensions : on garde le plan précédent plutôt que de le
       vider, et on ne met à jour que l'image du coloris. */
    if (connu) this.#etat.taille = taille;
    this.#etat.coloris = coloris ?? '';
    this.#dessiner();
  }

  /* ==== Rendu ============================================================= */

  #majCurseurs() {
    const piece = this.#etat.piece;
    const { largeur, longueur, valLargeur, valLongueur } = this.refs;
    if (!piece) return;

    const [lMin, pMin] = piece.min;
    const [lMax, pMax] = piece.max;
    this.#etat.L = borner(this.#etat.L, Math.min(lMin, lMax), Math.max(lMin, lMax));
    this.#etat.P = borner(this.#etat.P, Math.min(pMin, pMax), Math.max(pMin, pMax));

    if (largeur) {
      largeur.min = String(Math.min(lMin, lMax));
      largeur.max = String(Math.max(lMin, lMax));
      largeur.value = String(this.#etat.L);
      largeur.setAttribute('aria-valuetext', `${metres(this.#etat.L)} m`);
    }
    if (longueur) {
      longueur.min = String(Math.min(pMin, pMax));
      longueur.max = String(Math.max(pMin, pMax));
      longueur.value = String(this.#etat.P);
      longueur.setAttribute('aria-valuetext', `${metres(this.#etat.P)} m`);
    }
    if (valLargeur) valLargeur.textContent = `${metres(this.#etat.L)} m`;
    if (valLongueur) valLongueur.textContent = `${metres(this.#etat.P)} m`;
  }

  #dessiner() {
    const piece = this.#etat.piece;
    const format = this.#formats.find((f) => f.taille === this.#etat.taille) ?? this.#formats[0];
    if (!piece || !format) return;

    const gabarit = GABARITS[piece.gabarit] ?? GABARITS.salon;
    const L = this.#etat.L;
    const P = this.#etat.P;
    if (!(L > 0) || !(P > 0)) return;

    const { scene, dessin, gMeubles, tapisForme, tapisClip, tapisImage, coteH, coteV, pivot } = this.refs;

    /* Un tapis rond ne se pivote pas : le bouton disparaît, et l'état est
       ignoré sans être oublié — on le retrouve en revenant sur un rectangle. */
    const pivote = this.#etat.pivote && !format.rond;
    if (pivot) {
      pivot.hidden = format.rond;
      pivot.setAttribute('aria-pressed', String(pivote));
    }

    /* La scène garde le rapport réel de la pièce ; la trame vaut 50 cm sur
       chaque axe (le MVP réutilisait le même pas pour les deux — corrigé). */
    scene.style.aspectRatio = `${L} / ${P}`;
    scene.style.setProperty('--arp-maille-x', `${(50 / L) * 100}%`);
    scene.style.setProperty('--arp-maille-y', `${(50 / P) * 100}%`);
    this.#ajusterScene(L, P);

    dessin.setAttribute('viewBox', `0 0 ${L} ${P}`);

    /* Mobilier */
    const g = el('g');
    for (const meuble of gabarit.meubles(L, P)) {
      const dessinateur = MEUBLES[meuble.t];
      if (dessinateur) dessinateur(g, meuble);
    }
    gMeubles.replaceChildren(g);

    /* Tapis, centré sur l'ancre de la pièce.
       `tw`/`th` = l'EMPREINTE AU SOL, pivot compris ; `iw`/`ih` = la boîte de
       l'image AVANT rotation. Faire tourner un rectangle d'un quart de tour
       autour de son centre donne le rectangle transposé, de même centre :
       il suffit donc de dessiner l'image non pivotée centrée au même point,
       et de la tourner. Le contour, lui, épouse directement l'empreinte. */
    const iw = format.l;
    const ih = format.rond ? format.l : format.L;
    const tw = pivote ? ih : iw;
    const th = pivote ? iw : ih;
    const tx = (L - tw) / 2;
    const ty = gabarit.centreY(L, P) - th / 2;
    const rx = format.rond ? tw / 2 : 1;
    const ry = format.rond ? th / 2 : 1;
    const cx = tx + tw / 2;
    const cy = ty + th / 2;

    if (tapisForme) {
      tapisForme.setAttribute('x', String(tx));
      tapisForme.setAttribute('y', String(ty));
      tapisForme.setAttribute('width', String(tw));
      tapisForme.setAttribute('height', String(th));
      tapisForme.setAttribute('rx', String(rx));
      tapisForme.setAttribute('ry', String(ry));
    }

    /* L'image et son masque partagent la MÊME boîte non pivotée : la rotation
       posée sur l'image emmène le masque avec elle, les deux restent alignés
       quel que soit l'ordre d'application du navigateur. */
    const ix = cx - iw / 2;
    const iy = cy - ih / 2;
    if (tapisClip) {
      tapisClip.setAttribute('x', String(ix));
      tapisClip.setAttribute('y', String(iy));
      tapisClip.setAttribute('width', String(iw));
      tapisClip.setAttribute('height', String(ih));
      tapisClip.setAttribute('rx', String(format.rond ? iw / 2 : 1));
      tapisClip.setAttribute('ry', String(format.rond ? ih / 2 : 1));
    }

    if (tapisImage) {
      const source = this.#imageCourante();
      tapisImage.setAttribute('x', String(ix));
      tapisImage.setAttribute('y', String(iy));
      tapisImage.setAttribute('width', String(iw));
      tapisImage.setAttribute('height', String(ih));
      if (pivote) {
        tapisImage.setAttribute('transform', `rotate(90 ${cx} ${cy})`);
      } else {
        tapisImage.removeAttribute('transform');
      }
      if (source && tapisImage.getAttribute('href') !== source) tapisImage.setAttribute('href', source);
      tapisImage.style.display = source ? '' : 'none';
    }

    /* Cotes */
    if (coteH) coteH.textContent = `${metres(L)} m`;
    if (coteV) coteV.textContent = `${metres(P)} m`;

    /* État des boutons */
    for (const bouton of this.querySelectorAll('button[data-taille]')) {
      bouton.setAttribute('aria-pressed', String(bouton.getAttribute('data-taille') === format.taille));
    }
    for (const bouton of this.querySelectorAll('button[data-piece]')) {
      bouton.setAttribute('aria-pressed', String(bouton.getAttribute('data-piece') === piece.cle));
    }

    this.#juger({ L, P, format, tx, ty, tw, th, piece, gabarit, pivote });
  }

  /** Le plan ne dépasse jamais la hauteur utile de l'écran. */
  #ajusterScene(L, P) {
    const { plan, scene } = this.refs;
    if (!plan || !scene) return;
    const styles = getComputedStyle(plan);
    const dispo = plan.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
    if (!(dispo > 0)) return;
    const hMax = Math.min(520, Math.max(300, window.innerHeight * 0.6));
    scene.style.width = (dispo * P) / L > hMax ? `${Math.round((hMax * L) / P)}px` : '100%';
  }

  /** L'image du coloris sélectionné — c'est elle qu'on pose dans le plan. */
  #imageCourante() {
    const variantes = this.#donnees?.variantes ?? [];
    const { taille, coloris } = this.#etat;
    const exacte = variantes.find((v) => v.taille === taille && v.coloris === coloris);
    const parColoris = coloris ? variantes.find((v) => v.coloris === coloris) : null;
    const parTaille = variantes.find((v) => v.taille === taille);
    return exacte?.image || parColoris?.image || parTaille?.image || variantes[0]?.image || '';
  }

  /* ==== Le verdict ======================================================== */

  /** @param {any} contexte */
  #juger({ L, P, format, tx, ty, tw, th, piece, gabarit, pivote }) {
    const ancre = gabarit.ancre(L, P);
    const aireAncre = ancre.w * ancre.h;
    const aireTapis = format.rond ? Math.PI * (format.l / 2) ** 2 : format.l * format.L;

    const couverture = aireAncre > 0 ? inter({ x: tx, y: ty, w: tw, h: th }, ancre) / aireAncre : 0;
    const margeMin = Math.round(Math.min(tx, L - (tx + tw), ty, P - (ty + th)));
    const pctSol = (aireTapis / (L * P)) * 100;

    /* Format idéal : meilleure couverture avec au moins 25 cm de sol autour. */
    /* Les candidats sont évalués dans le sens de pose choisi : recommander un
       format qui ne tiendrait que dans l'autre sens serait un mauvais conseil. */
    const candidats = this.#formats.map((c) => {
      const cpiv = pivote && !c.rond;
      const cw = cpiv ? c.L : c.l;
      const ch = cpiv ? c.l : c.rond ? c.l : c.L;
      const cx = (L - cw) / 2;
      const cy = gabarit.centreY(L, P) - ch / 2;
      return {
        format: c,
        couv: aireAncre > 0 ? inter({ x: cx, y: cy, w: cw, h: ch }, ancre) / aireAncre : 0,
        marge: Math.min(cx, L - (cx + cw), cy, P - (cy + ch)),
      };
    });
    const ideal =
      candidats.filter((c) => c.marge >= 25).sort((a, b) => b.couv - a.couv)[0] ??
      [...candidats].sort((a, b) => b.marge - a.marge)[0];

    const conseil = this.#donnees?.conseilProduit || piece.conseil || '';
    const nom = format.taille;
    let etat;
    let titre;
    let texte;
    let recommande = null;

    if (margeMin < 0) {
      etat = 'attention';
      titre = 'Ce format ne rentre pas';
      texte = `Dans une pièce de ${metres(L)} × ${metres(P)} m, le ${nom} touche les murs. Il faut descendre d’un format.`;
    } else if (margeMin < 18) {
      etat = 'attention';
      titre = 'Trop juste aux murs';
      texte = `Il ne resterait que ${margeMin} cm de sol visible sur le côté le plus étroit. En dessous de 20 cm, l’œil lit une moquette mal posée, pas un tapis.`;
    } else if (couverture >= 0.9) {
      etat = 'ok';
      titre = 'La proportion est juste';
      texte = `${conseil} Avec le ${nom}, c’est le cas, et il reste ${margeMin} cm de sol tout autour pour que la pièce respire.`;
    } else if (couverture >= 0.62) {
      etat = 'ok';
      titre = 'Ça fonctionne';
      texte =
        `Le ${nom} tient l’espace correctement : il couvre ${Math.round(couverture * 100)} % de la zone de vie.` +
        (ideal && ideal.format.taille !== nom
          ? ` Le ${ideal.format.taille} irait chercher un peu plus loin sous les meubles : c’est celui que nous choisirions dans cette pièce.`
          : ' C’est le plus grand format que cette pièce accepte sans se refermer.');
      if (ideal && ideal.format.taille !== nom) recommande = ideal.format;
    } else {
      etat = 'attention';
      titre = 'Ce format va flotter';
      texte = `Dans cette pièce, le ${nom} ne couvre que ${Math.round(
        couverture * 100
      )} % de la zone de vie : il se lira comme un timbre-poste posé au milieu du sol. ${conseil}`;
      if (ideal && ideal.format.taille !== nom) recommande = ideal.format;
    }

    const {
      verdict,
      verdictTitre,
      verdictTexte,
      mesureSurface,
      mesureSol,
      mesureMur,
      adopter,
      adopterTexte,
      conseil: refConseil,
      dessin,
    } = this.refs;

    /* Le tout premier remplissage ne doit pas être lu à voix haute au chargement. */
    if (this.#premierRendu) verdict.setAttribute('aria-live', 'off');

    verdict.closest('[data-etat]')?.setAttribute('data-etat', etat);
    if (verdictTitre) verdictTitre.textContent = titre;
    if (verdictTexte) verdictTexte.textContent = texte.trim();
    if (mesureSurface) mesureSurface.textContent = `${(aireTapis / 1e4).toFixed(1).replace('.', ',')} m²`;
    if (mesureSol) mesureSol.textContent = `${Math.round(pctSol)} %`;
    if (mesureMur) mesureMur.textContent = `${Math.max(0, margeMin)} cm`;
    if (refConseil) refConseil.textContent = conseil;

    this.#ideal = recommande;
    if (adopter) {
      adopter.hidden = !recommande;
      if (recommande && adopterTexte) {
        adopterTexte.textContent = `${adopter.dataset.prefixe ?? 'Passer au'} ${recommande.taille}`;
      }
    }

    /* Le plan énonce le verdict en toutes lettres : c'est la seule façon qu'un
       lecteur d'écran accède à l'information. */
    const fin =
      margeMin < 0
        ? ` : il déborde de ${Math.abs(margeMin)} cm sur le côté le plus étroit.`
        : ` : il reste ${margeMin} cm de sol tout autour.`;
    const sens = pivote ? ', posé dans le sens de la largeur' : '';
    dessin.setAttribute(
      'aria-label',
      `${piece.nom} de ${metres(L)} m sur ${metres(P)} m. Tapis ${nom}${sens}. ${titre}${fin}`
    );

    if (this.#premierRendu) {
      this.#premierRendu = false;
      verdict.setAttribute('aria-live', 'polite');
    }
    /* Pour qui veut suivre le format retenu (la page « L'Arpentage »). */
    const variante =
      this.#donnees?.variantes.find((v) => v.taille === nom && v.coloris === this.#etat.coloris) ??
      this.#donnees?.variantes.find((v) => v.taille === nom);
    this.dispatchEvent(
      new CustomEvent('arpentage:etat', {
        bubbles: true,
        detail: { taille: nom, varianteId: variante?.id ?? null, etat, recommande: recommande?.taille ?? null },
      })
    );
  }
}

if (!customElements.get('arpentage-component')) {
  customElements.define('arpentage-component', ArpentageComponent);
}
